import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  confirmManualPayment,
  getPaymentStatus,
  initiatePaymentWithMethod,
  listLoans,
  listPayments,
} from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"
import { formatCurrency, formatDateTime } from "../utils/formatting"

export function PaymentsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const initialLoanId = searchParams.get("loanId") ?? ""
  const [selectedLoanId, setSelectedLoanId] = useState(initialLoanId)
  const [amountInput, setAmountInput] = useState("")
  const [paymentMethod, setPaymentMethod] = useState<"ECOCASH" | "INNBUCKS" | "MANUAL_PAYMENT">(
    "ECOCASH",
  )
  const [transactionReference, setTransactionReference] = useState("")
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10))
  const [manualPaymentMethod, setManualPaymentMethod] = useState<
    "BANK_TRANSFER" | "CASH_AT_AGENT" | "OTHER"
  >("BANK_TRANSFER")
  const [activePaymentId, setActivePaymentId] = useState<string | null>(null)
  const [formError, setFormError] = useState("")
  const [manualSuccess, setManualSuccess] = useState("")
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const loansQuery = useQuery({
    queryKey: ["portal-loans", "payment-select"],
    queryFn: () => listLoans(1, 50, { activeOnly: true }).then((response) => response.data),
  })

  const selectedLoan = useMemo(
    () => loansQuery.data?.find((loan) => loan.loanId === selectedLoanId) ?? null,
    [loansQuery.data, selectedLoanId],
  )

  useEffect(() => {
    if (selectedLoan) {
      setAmountInput(String((selectedLoan.outstandingBalance / 100).toFixed(2)))
    }
  }, [selectedLoan])

  const paymentsQuery = useInfiniteQuery({
    queryKey: ["portal-payments", selectedLoanId],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      listPayments({ pageParam, loanId: selectedLoanId || undefined }).then((response) => response),
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
  })

  const paymentStatusQuery = useQuery({
    queryKey: ["portal-payment-status", activePaymentId],
    queryFn: () => getPaymentStatus(activePaymentId!).then((response) => response.data),
    enabled: Boolean(activePaymentId),
    refetchInterval: (query) => (query.state.data?.status === "PROCESSING" ? 2500 : false),
  })

  useEffect(() => {
    if (paymentStatusQuery.data?.status === "COMPLETED") {
      queryClient.invalidateQueries({ queryKey: ["portal-dashboard"] })
      queryClient.invalidateQueries({ queryKey: ["portal-payments"] })
      queryClient.invalidateQueries({ queryKey: ["portal-loans"] })
    }
  }, [paymentStatusQuery.data?.status, queryClient])

  useEffect(() => {
    const target = sentinelRef.current
    if (!target || !paymentsQuery.hasNextPage || paymentsQuery.isFetchingNextPage) return

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        paymentsQuery.fetchNextPage()
      }
    })

    observer.observe(target)
    return () => observer.disconnect()
  }, [paymentsQuery])

  const paymentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLoanId) {
        throw new Error("Choose a loan before starting a payment.")
      }

      const amount = Math.round(Number(amountInput) * 100)
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error("Enter a valid payment amount.")
      }

      const pushMethod = paymentMethod === "INNBUCKS" ? "INNBUCKS" : "ECOCASH"
      return initiatePaymentWithMethod(selectedLoanId, amount, pushMethod)
    },
    onSuccess: (response) => {
      setActivePaymentId(response.data.paymentId)
      setFormError("")
      setManualSuccess("")
    },
    onError: (error) => {
      setFormError((error as Error).message)
    },
  })

  const manualMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLoanId) {
        throw new Error("Choose a loan before submitting a manual payment.")
      }

      const amount = Math.round(Number(amountInput) * 100)
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error("Enter a valid payment amount.")
      }

      if (!transactionReference.trim()) {
        throw new Error("Enter a transaction reference.")
      }

      return confirmManualPayment({
        loanId: selectedLoanId,
        transactionReference,
        amount,
        paymentDate,
        paymentMethod: manualPaymentMethod,
      })
    },
    onSuccess: async () => {
      setManualSuccess(t("payments.manualSuccess"))
      setFormError("")
      setTransactionReference("")
      await queryClient.invalidateQueries({ queryKey: ["portal-payments"] })
      await queryClient.invalidateQueries({ queryKey: ["portal-dashboard"] })
    },
    onError: (error) => {
      setFormError((error as Error).message)
      setManualSuccess("")
    },
  })

  const payments = paymentsQuery.data?.pages.flatMap((page) => page.data) ?? []
  const isManual = paymentMethod === "MANUAL_PAYMENT"

  return (
    <div className="page-stack">
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">{t("payments.makePayment")}</p>
            <h3>{t("payments.method")}</h3>
          </div>
        </div>

        {loansQuery.isLoading ? (
          <Skeleton height={180} />
        ) : (
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault()
              if (isManual) {
                manualMutation.mutate()
              } else {
                paymentMutation.mutate()
              }
            }}
          >
            <label>
              {t("payments.loan")}
              <select onChange={(event) => setSelectedLoanId(event.target.value)} value={selectedLoanId}>
                <option value="">Select a loan</option>
                {(loansQuery.data ?? []).map((loan) => (
                  <option key={loan.loanId} value={loan.loanId}>
                    {loan.loanId} · {formatCurrency(loan.currency, loan.outstandingBalance)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              {t("payments.amount")}
              <input
                inputMode="decimal"
                onChange={(event) => setAmountInput(event.target.value)}
                placeholder="0.00"
                value={amountInput}
              />
            </label>

            {selectedLoan ? (
              <div className="hint-copy">
                {t("payments.outstanding")}:{" "}
                {formatCurrency(selectedLoan.currency, selectedLoan.outstandingBalance)}
              </div>
            ) : null}

            <label>
              {t("payments.method")}
              <select
                onChange={(event) =>
                  setPaymentMethod(event.target.value as "ECOCASH" | "INNBUCKS" | "MANUAL_PAYMENT")
                }
                value={paymentMethod}
              >
                <option value="ECOCASH">EcoCash</option>
                <option value="INNBUCKS">Innbucks</option>
                <option value="MANUAL_PAYMENT">Manual Payment</option>
              </select>
            </label>

            <div className="hint-copy">
              {paymentMethod === "ECOCASH"
                ? t("payments.methodCopy.ecocash")
                : paymentMethod === "INNBUCKS"
                  ? t("payments.methodCopy.innbucks")
                  : t("payments.methodCopy.manual")}
            </div>

            {isManual ? (
              <>
                <label>
                  {t("payments.manualReference")}
                  <input
                    onChange={(event) => setTransactionReference(event.target.value)}
                    value={transactionReference}
                  />
                </label>
                <label>
                  {t("payments.manualDate")}
                  <input
                    onChange={(event) => setPaymentDate(event.target.value)}
                    type="date"
                    value={paymentDate}
                  />
                </label>
                <label>
                  {t("payments.manualType")}
                  <select
                    onChange={(event) =>
                      setManualPaymentMethod(
                        event.target.value as "BANK_TRANSFER" | "CASH_AT_AGENT" | "OTHER",
                      )
                    }
                    value={manualPaymentMethod}
                  >
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CASH_AT_AGENT">Cash at Agent</option>
                    <option value="OTHER">Other</option>
                  </select>
                </label>
              </>
            ) : null}

            {formError ? <div className="status-banner error">{formError}</div> : null}
            {manualSuccess ? <div className="status-banner success">{manualSuccess}</div> : null}

            <button
              className="primary-button"
              disabled={paymentMutation.isPending || manualMutation.isPending}
              type="submit"
            >
              {isManual
                ? manualMutation.isPending
                  ? t("payments.submittingManual")
                  : t("payments.submitManual")
                : paymentMutation.isPending
                  ? t("payments.startingPayment")
                  : t("payments.startPayment")}
            </button>
          </form>
        )}
      </section>

      {paymentStatusQuery.data ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">{t("payments.status")}</p>
              <h3>{paymentStatusQuery.data.status.replaceAll("_", " ")}</h3>
            </div>
          </div>
          <dl className="metric-grid">
            <div>
              <dt>{t("payments.reference")}</dt>
              <dd>{paymentStatusQuery.data.providerReference ?? "-"}</dd>
            </div>
            <div>
              <dt>{t("payments.amount")}</dt>
              <dd>{formatCurrency(paymentStatusQuery.data.currency, paymentStatusQuery.data.amount)}</dd>
            </div>
          </dl>
          {paymentStatusQuery.data.failureReason ? (
            <div className="status-banner error">{paymentStatusQuery.data.failureReason}</div>
          ) : null}
          {paymentStatusQuery.data.status === "COMPLETED" ? (
            <div className="status-banner success">
              {t("payments.confirmed", { id: paymentStatusQuery.data.repaymentId })}
            </div>
          ) : (
            <p className="subtle">
              {paymentStatusQuery.data.provider === "INNBUCKS"
                ? t("payments.waiting.innbucks")
                : t("payments.waiting.ecocash")}
            </p>
          )}
        </section>
      ) : null}

      <section className="section-header">
        <div>
          <p className="eyebrow">{t("payments.history")}</p>
          <h3>{t("payments.historyTitle")}</h3>
        </div>
      </section>

      <div className="card list-card">
        {paymentsQuery.isLoading ? (
          <Skeleton height={200} />
        ) : payments.length === 0 ? (
          <p className="empty-copy">{t("payments.empty")}</p>
        ) : (
          <>
            {payments.map((payment) => (
              <div className="list-row" key={payment.paymentId}>
                <div>
                  <strong>{formatCurrency(payment.currency, payment.amount)}</strong>
                  <p className="subtle">
                    {payment.loanId} · {payment.transactionReference ?? "No reference"}
                  </p>
                  {payment.rejectionReason ? (
                    <p className="subtle">
                      {t("payments.rejection")}: {payment.rejectionReason}
                    </p>
                  ) : null}
                </div>
                <div className="align-right">
                  <strong>{payment.status}</strong>
                  <p className="subtle">{formatDateTime(payment.paymentDate)}</p>
                </div>
              </div>
            ))}
            <div ref={sentinelRef} />
            {paymentsQuery.isFetchingNextPage ? <p className="subtle">Loading more...</p> : null}
          </>
        )}
      </div>
    </div>
  )
}
